<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';
import Button from 'dashboard/components-next/button/Button.vue';

const { content, contentAttributes } = useMessageContext();

const cardItems = computed(() => {
  return contentAttributes.value?.items || [];
});

const isTrustpilot = action => {
  return (
    (action.text || '').toLowerCase().includes('trustpilot') ||
    (action.uri || '').toLowerCase().includes('trustpilot')
  );
};

const isG2 = action => {
  return (
    (action.text || '').toLowerCase().includes('g2') ||
    (action.uri || '').toLowerCase().includes('g2')
  );
};

const isGoogle = action => {
  return (
    (action.text || '').toLowerCase().includes('google') ||
    (action.uri || '').toLowerCase().includes('google')
  );
};

const openLink = uri => {
  if (uri) {
    window.open(uri, '_blank', 'noopener,noreferrer');
  }
};
</script>

<template>
  <div class="flex flex-col gap-2 max-w-[28rem]">
    <!-- Render the main message text as a standard text bubble first -->
    <BaseBubble class="px-4 py-2.5" data-bubble-name="cards-text">
      <span
        v-dompurify-html="content"
        class="text-sm font-normal leading-relaxed text-n-slate-12"
      />
    </BaseBubble>

    <!-- Render the cards -->
    <div
      v-for="(item, index) in cardItems"
      :key="index"
      class="flex flex-col gap-2.5 p-4 bg-n-background dark:bg-n-solid-3 border border-n-strong rounded-2xl shadow-sm self-start w-full"
    >
      <img
        v-if="item.media_url"
        class="w-full object-cover max-h-[160px] rounded-xl mb-1"
        :src="item.media_url"
        alt="Card image"
      />
      <h4
        v-if="item.title"
        class="text-base font-semibold leading-snug text-n-slate-12"
      >
        {{ item.title }}
      </h4>
      <p v-if="item.description" class="text-sm leading-relaxed text-n-slate-11">
        {{ item.description }}
      </p>

      <!-- Render the actions/buttons -->
      <div
        v-if="item.actions && item.actions.length"
        class="flex flex-col gap-2 mt-2"
      >
        <button
          v-for="(action, aIndex) in item.actions"
          :key="aIndex"
          class="w-full flex items-center justify-center gap-2.5 px-4 py-2 rounded-xl text-sm font-medium transition-all shadow-sm border cursor-pointer select-none"
          :style="{
            background: isTrustpilot(action)
              ? '#00B67A'
              : isG2(action)
              ? '#FF492C'
              : isGoogle(action)
              ? '#ffffff'
              : 'transparent',
            borderColor: isTrustpilot(action)
              ? '#00B67A'
              : isG2(action)
              ? '#FF492C'
              : isGoogle(action)
              ? '#e2e8f0'
              : '#e2e8f0',
            color: isGoogle(action)
              ? '#1e293b'
              : isTrustpilot(action) || isG2(action)
              ? '#ffffff'
              : '#475569',
          }"
          @click="openLink(action.uri)"
        >
          <svg
            v-if="isTrustpilot(action)"
            class="w-5 h-5 flex-shrink-0"
            viewBox="0 0 32 32"
            fill="none"
          >
            <circle cx="16" cy="16" r="16" fill="#00B67A" />
            <polygon
              points="16,7 18.47,13.8 25.7,13.8 20.12,17.87 22.59,24.67 16,20.6 9.41,24.67 11.88,17.87 6.3,13.8 13.53,13.8"
              fill="#fff"
            />
          </svg>
          <svg
            v-else-if="isGoogle(action)"
            class="w-5 h-5 flex-shrink-0"
            viewBox="0 0 32 32"
            fill="none"
          >
            <circle cx="16" cy="16" r="16" fill="#fff" />
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="white"
              stroke="#E8E8E8"
              stroke-width="0.5"
            />
            <path
              d="M24.5 16.2c0-.7-.1-1.4-.2-2H16v3.8h4.8c-.2 1.1-.9 2-1.8 2.6v2.1h3c1.7-1.6 2.5-3.9 2.5-6.5z"
              fill="#4285F4"
            />
            <path
              d="M16 25c2.4 0 4.5-.8 6-2.2l-3-2.1c-.8.5-1.8.8-3 .8-2.3 0-4.3-1.6-5-3.7H7.9v2.2C9.4 23.1 12.5 25 16 25z"
              fill="#34A853"
            />
            <path
              d="M11 17.8c-.2-.5-.3-1.1-.3-1.8s.1-1.3.3-1.8V12H7.9C7.3 13.2 7 14.6 7 16s.3 2.8.9 4l3.1-2.2z"
              fill="#FBBC05"
            />
            <path
              d="M16 10.3c1.3 0 2.5.5 3.4 1.3l2.5-2.5C20.5 7.7 18.4 7 16 7c-3.5 0-6.6 1.9-8.1 4.8l3.1 2.2c.7-2.1 2.7-3.7 5-3.7z"
              fill="#EA4335"
            />
          </svg>
          <svg
            v-else-if="isG2(action)"
            class="w-5 h-5 flex-shrink-0"
            viewBox="0 0 32 32"
            fill="none"
          >
            <circle cx="16" cy="16" r="16" fill="#FF492C" />
            <text
              x="16"
              y="21"
              text-anchor="middle"
              font-family="Arial,sans-serif"
              font-weight="900"
              font-size="13"
              fill="#fff"
            >
              G2
            </text>
          </svg>
          <span>{{ action.text }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
